import { dependentKeyCompat } from '@ember/object/compat';
import { type Registry as Services, service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import type Model from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import { dedupeTracked } from 'tracked-toolbox';

type RelationshipState = {
  type: string | null;
  value: string | string[] | null;
};

type StoredRelationship = {
  kind: string;
  name: string | symbol | number;
  state: RelationshipState;
};

export interface DirtyRelationshipModel {
  hasDirtyRelationships: boolean;
  rollbackRelationships: () => void;
  updateRelationships: () => void;
}

const xor = <T>(a: T[], b: T[]): T[] => {
  if (a === null && b === null) {
    return [];
  }

  if (a === null) {
    return b;
  }

  if (b === null) {
    return a;
  }

  const allValues: T[] = [a, b].flat();
  return allValues.filter(
    (value, _, array) => array.indexOf(value) === array.lastIndexOf(value),
  );
};

/**
 * Changing relationships on ED Model does not update `hasDirtyAttributes` property.
 * This decorator is a workaround for that.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function (): any {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function <T extends { new (...args: any[]): Model }>(Class: T) {
    class DecoratedClass extends Class {
      @service declare store: Services['store'];

      /**
       * This array contains the persisted relationships.
       * Updated when Model initialized or invoked save
       */
      @dedupeTracked _storedRelationships: StoredRelationship[] = [];

      /**
       * `dependentKeyCompat` for exposing this getter to Ember's classic computed property and observer systems,
       * so they can watch it for changes
       *
       * If the Model is in isDestroyed or isDestroying state, this returns false to stop checking for relationship changes
       * If the Model is in isNew state, we return true (same as `hasDirtyAttributes`)
       */
      @cached
      @dependentKeyCompat
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      get hasDirtyRelationships() {
        const { isDestroyed, isDestroying, isNew, _storedRelationships } = this;

        if (isDestroyed || isDestroying) {
          return false;
        }
        if (Boolean(isNew) || isEmpty(_storedRelationships)) {
          return true;
        }

        /**
         * Populating this array with relationships to trigger recompute on this getter
         * when a relationship is changed
         * e.g. [this.foos, this.bar, this.user]
         */
        const _relationshipReferences = [];

        /**
         * Populating this array with objects that contains
         * the current relationships by
         * - name: String
         * - kind: String<'belongsTo' || 'hasMany'>
         * - state: Object
         */
        const _currentState: StoredRelationship[] = [];

        this.eachRelationship((name, { kind }) => {
          // @ts-expect-error: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'DecoratedClass'.
          _relationshipReferences.push(this[name]);

          // @ts-expect-error: Argument of type 'MaybeRelationshipFields<this>' is not assignable to parameter of type 'Exclude<keyof this, keyof Model>'.
          _currentState.push({ name, kind, state: this._getState(name, kind) });
        });

        /**
         * Compare current relationships to the stored relationships,
         * if the relationship type is:
         * - `belongsTo`: we do a simple not-eq check
         * - `hasMany`: we do a xor check, to eliminate ids order
         *  - ['1', '2', '3'] should be eq to ['2', '3', '1'], this should not be dirty
         *  - ['2', '3'] or [] should be different than ['1'] or []
         */
        const isDirty = _currentState.some(
          ({ kind, state, name: relationshipName }) => {
            const storedRelationship = _storedRelationships.find(
              ({ name }) => name === relationshipName,
            );

            // if current relationship is not in the stored relationships array, return true as dirty
            if (!storedRelationship) {
              return true;
            }

            // if the remoteType on relationship ref returned 'link', compare the links
            if (state?.type === 'link') {
              return storedRelationship.state?.value !== state?.value;
            }

            // type is null, because the ref type is 'id' or 'ids'
            return kind === 'belongsTo'
              ? storedRelationship.state?.value !== state?.value
              : isPresent(
                  xor(
                    storedRelationship.state?.value as string[],
                    state?.value as string[],
                  ),
                );
          },
        );

        return isDirty;
      }

      /**
       * This method is for getting the reference value and type
       * type is `null` if the relationship reference `remoteType` is 'id' or 'ids'
       * type is `link` if the relationship reference `remoteType` is 'link'
       */
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      _getState(name: Exclude<keyof this, keyof Model>, kind: string) {
        let state;

        if (kind === 'hasMany') {
          // @ts-expect-error: Argument of type 'Exclude<keyof this, keyof Model>' is not assignable to parameter of type 'MaybeHasManyFields<this>'.
          const hasManyRef = this.hasMany(name);
          if (!hasManyRef.value()) {
            return { type: null, value: null };
          }

          if (hasManyRef.remoteType() === 'ids') {
            state = {
              type: null,
              value: hasManyRef.ids(),
            };
          } else if (hasManyRef.remoteType() === 'link') {
            state = {
              type: 'link',
              value: hasManyRef.link(),
            };
          }
        }

        if (kind === 'belongsTo') {
          // @ts-expect-error: rgument of type 'Exclude<keyof this, keyof Model>' is not assignable to parameter of type 'keyof this & string & (keyof this & string extends _MaybeBelongsToFields<this> ? keyof this & string : never)'.
          const belongsToRef = this.belongsTo(name);

          if (!belongsToRef.value()) {
            return { type: null, value: null };
          }

          if (belongsToRef.remoteType() === 'id') {
            state = {
              type: null,
              value: belongsToRef.id(),
            };
          } else if (belongsToRef.remoteType() === 'link') {
            state = {
              type: 'link',
              value: belongsToRef.link(),
            };
          }
        }

        return state as RelationshipState;
      }

      /**
       * This function stores the persisted relationships
       * Called during initialization or after save
       */
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      _storeRelationships() {
        this._storedRelationships = [];
        if (this.isDestroyed || this.isDestroying) {
          return;
        }

        const relationships: StoredRelationship[] = [];

        this.eachRelationship((name, { kind }) => {
          relationships.push({
            name,
            kind,
            // @ts-expect-error: Argument of type 'MaybeRelationshipFields<this>' is not assignable to parameter of type 'Exclude<keyof this, keyof Model>'.
            state: this._getState(name, kind),
          });
        });

        this._storedRelationships = relationships;
      }

      /**
       * This function rolls back the relationships to the stored relationships
       * if the stored relationship is null, we set the relationship to null
       * if the stored relationship is not null, we set the relationship to the record from the store
       * based on the id and relationship type
       *
       * only runs if there's a dirty relationship so we avoid unnecessary runs
       */
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      rollbackRelationships() {
        if (this.isDestroyed || this.isDestroying) {
          return;
        }
        if (!this.hasDirtyRelationships) {
          return;
        }

        this.eachRelationship((name, { kind, type }) => {
          const storedRelationship = this._storedRelationships.find(
            (rel) => rel.name === name && rel.kind === kind,
          );

          if (!storedRelationship) {
            return;
          }

          if (kind === 'belongsTo') {
            if (storedRelationship.state.value === null) {
              // @ts-expect-error its safe
              this[name] = null;
            } else {
              // @ts-expect-error: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'DecoratedClass'.
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              this[name] = this.store.peekRecord(
                type,
                storedRelationship.state.value as string,
              );
            }
          }

          if (kind === 'hasMany') {
            if (storedRelationship.state.value === null) {
              // @ts-expect-error its safe
              this[name] = [];
            } else {
              const records = (storedRelationship.state.value as string[]).map(
                (id) => this.store.peekRecord(type, id),
              );
              // @ts-expect-error we don't know the type of the relationship
              this[name] = records;
            }
          }
        });
      }

      /**
       * Extending the save function to update the stored relationship
       * after successful save
       */
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      save(...args: Parameters<Model['save']>) {
        const promise = super.save(...args);

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        promise.then(() => this._storeRelationships());
        return promise;
      }

      /**
       * This function is for updating the stored relationships
       * invoke this function when you want to update the stored relationships
       * for e.g. not using save method to persist the changes
       */
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      updateRelationships() {
        this._storeRelationships();
      }
    }

    // reassign the original class name as the name of the decorated class
    Object.defineProperty(DecoratedClass, 'name', { value: Class.name });

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { init } = DecoratedClass.prototype;
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    DecoratedClass.prototype.init = function (
      ...args: Parameters<Model['init']>
    ) {
      init.call(this, ...args);
      this._storeRelationships();
    };

    return DecoratedClass;
  };
}
